import getEnvironmentVariables from '../../helpers/getEnvironmentVariables'
import { buildLoginUrl } from '../../helpers/loginUrl'
export const endpoints = {
    // Event types are user-typed: encoded so a "/", "#" or "?" cannot break
    // the path.
    getPipelines: (eventID) => `/comms/pipeline/${encodeURIComponent(eventID)}`,
    // Every active pipeline, newest first, one page at a time (status-created_on
    // index). What the page lists when no event type is picked.
    getActivePipelines: '/comms/v2/pipelines/status',
    // How many pipelines have a status (count-only query, no rows read).
    countPipelines: '/comms/v2/pipelines/count',
    // The same pages narrowed by `search_param` (pipeline name or event type).
    searchPipelines: '/comms/v2/pipelines/status/search',
    // Receiver types the service accepts, and the receiver each existing event
    // type's pipelines send to.
    getPipelineEnums: '/comms/v2/pipeline/create/enums',
    // The sample event an integrating service sends for an event type.
    getPipelinePayload: '/comms/v2/get_payload',
    // The v2 pipeline routes are what the production comms app uses. The v1
    // POST/PUT /comms/pipeline require an `id` on create and a single
    // `comms` object, so they rejected the wizard's body with 422.
    createPipeline: '/comms/v2/pipeline',
    updatePipeline: (pipelineID) =>
        `/comms/v2/pipeline/${encodeURIComponent(pipelineID)}`,
    getPipeline: (pipelineID) =>
        `/comms/v2/pipeline/${encodeURIComponent(pipelineID)}`,
    deletePipeline: (pipelineID) => `/comms/pipeline/${pipelineID}`,
    // A pipeline name may hold "/", "#" or "?"; the page route encodes it and
    // useParams decodes it, so it is encoded again here.
    getPipelineHistory: (pipelineName) =>
        `/comms/pipeline/history/${encodeURIComponent(pipelineName)}`,
    getEventTypes: '/comms/pipelines/event-types',
    getTemplates: '/comms/templates',
    // Amazon Pinpoint templates (SMS: the one-time password and demo flows
    // send them), listed read-only beside the CommTemplates rows.
    getPinpointTemplates: (commType) =>
        `/comms/templates/pinpoint/${encodeURIComponent(commType)}`,
    getPinpointTemplate: (commType, templateName) =>
        `/comms/templates/pinpoint/${encodeURIComponent(
            commType
        )}/${encodeURIComponent(templateName)}`,
    getTemplateDataJson: (templateID) =>
        `/comms/template/json_template_data/${templateID}`,
    getTemplateData: (templateID) => `/comms/template/preview/${templateID}`,
    // The template row: subject, declared `others`, and the body read from S3
    // as `extracted_body`.
    getTemplate: (templateID) =>
        `/comms/v2/template/${encodeURIComponent(templateID)}`,
    getApproverTypes: `/comms/approverTypes`,
    saveTemplateData: (templateID) => `/comms/template/${templateID}`,
    createTemplate: '/comms/v2/template',
    getTemplateCategories: '/comms/v2/template-categories',
    // WhatsApp Utility review check: POST with the body as the `message`
    // query parameter; and its rewrite: POST {message, percentage, reasons,
    // recommendations}.
    approvalProbability: '/comms/v2/template/approval_probability',
    revisedMessage: '/comms/v2/template/revised_message',
    // The whole row is replaced: send it as saved with the changes on top
    // (helpers/templateForm editPayload).
    updateTemplate: (templateID) =>
        `/comms/v2/template/${encodeURIComponent(templateID)}`,
    // Verified sender addresses, managed in the old app's "Manage Sender Emails".
    getSenderEmails: '/comms/senderEmails',
    createSenderEmail: '/comms/senderEmail',
    // Retires the address (soft delete); templates using it keep it.
    deleteSenderEmail: (id) => `/comms/senderEmail/${encodeURIComponent(id)}`,
    // Multipart `file`, plus `commtype=whatsapp` for a WhatsApp header's
    // media (stored under whatsapp_attachments/); answers {status, message,
    // s3_path}, s3_path "s3://<folder>/<epoch>_<file name>".
    uploadFile: '/comms/v2/files/upload',
    sendTestCommunication: '/comms/template/test',
    getTestCommunicationStatus: (executionId) =>
        `/comms/template/test/status/${encodeURIComponent(executionId)}`,
    sendForApproval: (templateID) =>
        `/comms/template/submitApproval/${templateID}`,
    // Soft delete (deleted_status, status inactive); a WhatsApp template is
    // also deleted in Meta.
    deleteTemplate: (templateID) =>
        `/comms/v2/template/delete/${encodeURIComponent(templateID)}`,
    approveTemplate: (data) =>
        `/comms/template/status/${data.templateID}/${data.status}`,
    bulkSendComms: '/comms/v2/bulk_comms',
    // Must exist (same event_id) before /v2/bulk_comms accepts the send.
    createCommBulkLog: '/comms/v2/create_comm_bulk_log',
    // Bulk Send (services/bulkSendService.js, services/shortLinkService.js):
    // what an event needs (`?event_type=`: absolute_time, niceness, comm,
    // others), its scheduled sends, cancelling one, and short links (a
    // server-sent event stream, read with fetch).
    eventTypeDetails: '/comms/v2/pipelines/event_type',
    scheduledSends: '/comms/v2/get_schedule_comms',
    cancelScheduledSend: '/comms/v2/disable_scheduled_comms',
    shortLinksStream: '/comms/v2/generate_short_links_stream',
    getCommReports: '/comms/v2/generate_comm_reports',
    getCommReport: (commId) => `/comms/v2/get_comm_report?comm_id=${commId}`,
    // Comms sent per channel and event type in [start_time, end_time] (epoch
    // seconds); the start may be at most 180 days back.
    commVolumeReport: '/comms/v2/comm_volume_report',
    aiSettingsStatus: '/comms/ai/settings/status',
    aiSettingsKey: '/comms/ai/settings/key',
    aiSettingsProvider: '/comms/ai/settings/provider',
    aiSettingsModel: '/comms/ai/settings/model',
    aiGenerateTemplate: '/comms/ai/generate-template',
    aiTranslateTemplate: '/comms/ai/translate-template',
    aiValidateCSV: '/comms/ai/validate-csv',
    aiBulkPreview: '/comms/ai/bulk-preview',
    aiCampaignBrief: '/comms/ai/campaign-brief',
    aiPerformanceInsights: '/comms/ai/performance-insights',
    aiTemplateVariants: '/comms/ai/template-variants',
    aiUnsubscribeRisk: '/comms/ai/unsubscribe-risk',
    aiAudienceOverlap: '/comms/ai/audience-overlap',
    aiCRMLookup: '/comms/ai/crm-lookup',
    aiCommsFinder: '/comms/ai/comms-finder',
    // Per-domain sending warmup. Keyed by domain so a new marketing subdomain
    // is a config row rather than a code change.
    // Holidays (CommHolidays): job-holiday-reminder sends each holiday's
    // events through the pipeline whose event type is the holiday id.
    holidays: '/comms/holidays',
    holidaysSearch: '/comms/holidays/status/search',
    createHoliday: '/comms/holiday',
    bulkHolidays: '/comms/bulk-holiday',
    editHoliday: (holidayID) =>
        `/comms/holidays/edit/${encodeURIComponent(holidayID)}`,
    holidayStatus: (holidayID, status) =>
        `/comms/holiday/${encodeURIComponent(holidayID)}/${encodeURIComponent(
            status
        )}`,

    // Trainer dashboard notifications (services/trainerNotificationService.js):
    // authored in comms, shown on the trainer dashboard's strip and Updates page.
    trainerNotifications: '/comms/trainer-notifications',
    trainerNotification: (id) =>
        `/comms/trainer-notifications/${encodeURIComponent(id)}`,
    trainerNotificationDeactivate: (id) =>
        `/comms/trainer-notifications/${encodeURIComponent(id)}/deactivate`,
    trainerNotificationAudienceOptions:
        '/comms/trainer-notifications/audience-options',
    // Served by TrainerManagementService on the same API host: how many
    // active trainers an audience reaches.
    trainerNotificationAudienceCount:
        '/tms/v2/trainer-notifications/audience-count',

    sendingDomains: '/comms/sending-domains',
    sendingDomain: (domain) => `/comms/sending-domains/${domain}`,
    sendingDomainStatus: '/comms/sending-domains/status',
    sendingDomainEvaluate: '/comms/sending-domains/evaluate',
    // Dry-run a bulk send: audience arithmetic, warmup position, content checks.
    preSendReport: '/comms/v2/bulk_comms/pre_send_report',
    // Cross-channel segmentation (preview only; never enqueues).
    segmentsPreview: '/comms/segments/preview',
    segmentOperators: '/comms/segments/operators',

    // BhanzuBot and the Comms Guide (service-comms routers/assistant.py).
    // The person comes from the admin cookie, never the request body.
    assistantGuide: '/comms/v2/assistant/guide',
    assistantConversations: '/comms/v2/assistant/conversations',
    assistantConversation: (conversationID) =>
        `/comms/v2/assistant/conversations/${encodeURIComponent(
            conversationID
        )}`,
    assistantMessages: (conversationID) =>
        `/comms/v2/assistant/conversations/${encodeURIComponent(
            conversationID
        )}/messages`,

    // Sending reputation. Read-only.
    reputationAccount: '/comms/reputation/account',
    reputationCampaigns: '/comms/reputation/campaigns',
    // A function, not a string: it reads the page's destUrl when the user
    // clicks "Sign in", not once at module load (which went stale and sent
    // the literal "null"). return_url is the app root on the CURRENT host,
    // including any base path, so admin-auth returns to this app. See
    // buildLoginUrl.
    login: () =>
        buildLoginUrl({
            baseURL: getEnvironmentVariables().baseURL,
            origin: window.location.origin,
            basePath: process.env.PUBLIC_URL,
            destUrl: new URLSearchParams(window.location.search).get('destUrl'),
        }),
}
